import { HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { BackgroundJobNotificationService } from 'Src/ng2/shared/services/background-job-notification-service/background-job-notification-service';
import { BackgroundJob } from 'Src/ng2/shared/services/background-job/background-job.service';
import * as milestoneActions from '../actions/milestone-actions';
import { LoadPostsecondaryCalculatedFields } from '../actions/students-actions/calculated-fields-action';
import { ApiService } from './../../shared/services/api-service/api-service';
import { ISnackBarData, SnackBarService } from './../../shared/services/snackbar/snackbar.service';
import { MILESTONE_PROJECTION } from './projections';

@Injectable()
export class MilestoneEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private snackBarService: SnackBarService,
    private store: Store<any>,
    private backgroundJob: BackgroundJob,
    private injector: Injector,
    private backgroundJobNotificationService: BackgroundJobNotificationService,
  ) {}

  snackBarData: ISnackBarData = { message: '', duration: 2000 };

  @Effect()
  loadAllMilestones$ = this.actions$.pipe(
    ofType(milestoneActions.LOAD_ALL_MILESTONES),
    switchMap((action: { type: string; payload: any }) => {
      const { schoolId } = action.payload;
      const projection = MILESTONE_PROJECTION;
      const payload = { schoolId, projection };
      return this.apiService.getMilestones(payload).pipe(
        switchMap(response => {
          const milestones = response.data || response;
          return [new milestoneActions.LoadAllMilestonesSuccess(milestones)];
        }),
        catchError(error => of(new milestoneActions.LoadAllMilestonesFail(error))),
      );
    }),
  );

  @Effect()
  loadStudentMilestones$ = this.actions$.pipe(
    ofType(milestoneActions.LOAD_STUDENT_MILESTONES),
    switchMap((action: { type: string; payload: any }) => {
      const schoolId = action.payload.schoolId;
      const studentId = action.payload.studentId;
      const payload = { schoolId, studentId };
      return this.apiService.getMilestones(payload).pipe(
        switchMap(response => {
          const milestones = response.data || response;
          return [new milestoneActions.LoadStudentMilestonesSuccess({ milestones, studentId })];
        }),
        catchError(error => of(new milestoneActions.LoadStudentMilestonesFail(error))),
      );
    }),
  );

  @Effect()
  updateMilestone$ = this.actions$.pipe(
    ofType(milestoneActions.UPDATE_MILESTONE),
    switchMap((action: { type: string; payload: any }) => {
      const { docId, patch, schoolId } = action.payload;

      return this.apiService.patchMilestone(docId, patch).pipe(
        switchMap(milestone => {
          // refectch calcfields for current user after milestone changes (JE)
          this.store.dispatch(new LoadPostsecondaryCalculatedFields({ schoolId }));
          return [new milestoneActions.UpdateMilestoneSuccess(milestone)];
        }),
        catchError(error => {
          return of(new milestoneActions.UpdateMilestoneFail(error));
        }),
      );
    }),
  );

  @Effect({ dispatch: false })
  updateMilestones$ = this.actions$.pipe(
    ofType<any>(milestoneActions.BULK_UPDATE_MILESTONES),
    withLatestFrom(this.store),
    switchMap(([action]) => {
      const payload = action.payload;
      const { milestoneIds, status, where } = payload;
      const backgroundJobSpinner = this.injector.get('BackgroundJobSpinner');
      const httpResponseObsv$: Observable<HttpResponse<any>> = this.apiService.bulkPatchMilestones(
        milestoneIds,
        status,
        where,
      );
      return httpResponseObsv$.pipe(
        take(1),
        switchMap(response => {
          const jobId = response.headers.get('nv-background-jobs');
          const promise = (this.backgroundJob as any)(jobId).promise();
          backgroundJobSpinner.activateSpinner('Updating students...', promise, null);
          return of(promise);
        }),
        tap(promise => {
          promise.then((res: any) => {
            this.backgroundJobNotificationService.sendMessage({ backgroundJob: 'BulkMilestoneUpdate' });
            this.snackBarService.showBatchActionToast({ success: true });
            // refectch calcfields for current user after milestone changes (JE)
            this.store.dispatch(new LoadPostsecondaryCalculatedFields({ schoolId: payload.where.schoolId }));
          });

          return of({});
        }),
        catchError(error => {
          this.snackBarService.showBatchActionToast({ success: false });
          return of({});
        }),
      );
    }),
  );
}
